// The ERP's frame: the top bar (Montrose, quick actions) and the left menu - each person sees only
// what their role allows, with a count of what's waiting for their approval.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { group: "", items: [
    { to: "/", label: "Home", exact: true },
    { to: "/approvals", label: "Approvals", badge: true },
  ] },
  { group: "Operations", items: [
    { to: "/ops", label: "Truck board", perms: ["dash.ops"] },
    { to: "/trips", label: "Trips", perms: ["trips.view"] },
    { to: "/yard", label: "Yard gate", perms: ["yard.log", "dash.ops", "dash.owner", "dash.workshop", "audit.view"] },
    { to: "/depot", label: "Fuel depot", perms: ["fuel.issue", "fuel.dip", "fuel.receive"] },
    { to: "/fuel", label: "Fuel analysis", perms: ["dash.fuel"] },
    { to: "/accidents", label: "Accidents & insurance", perms: ["accidents.report", "accidents.manage", "dash.owner", "books.view", "audit.view"] },
    { to: "/tyres", label: "Tyres", perms: ["tyres.manage", "jobcard.open", "jobcard.approve", "stores.issue", "registers.view", "books.view", "dash.owner", "dash.workshop"] },
    { to: "/jobcards", label: "Job cards", perms: ["jobcard.open", "jobcard.approve", "jobcard.close", "tyres.manage", "stores.issue", "stores.manage", "books.view", "dash.owner", "dash.workshop"] },
  ] },
  { group: "Money", items: [
    { to: "/spend", label: "Requests & payments", perms: ["expense.request", "float.request", "float.pay", "float.approve", "float.topup"] },
    { to: "/salaries", label: "Salaries upload", perms: ["payroll.view"] },
    { to: "/stores", label: "Stores", perms: ["stores.receive", "stores.issue", "stores.count", "stores.manage", "lpo.request", "lpo.create", "jobcard.approve", "jobcard.open", "books.view", "dash.owner", "dash.workshop", "approve.final"] },
    { to: "/purchasing", label: "Purchasing", perms: ["lpo.request", "lpo.create", "lpo.approve.l1", "lpo.approve.l2", "lpo.approve.l3", "lpo.send", "suppliers.manage", "stores.receive", "supplier_invoice.capture", "payment.initiate", "books.view", "dash.owner", "jobcard.approve"] },
    { to: "/payouts", label: "M-Pesa payouts", perms: ["float.pay", "float.approve", "books.view", "dash.owner"] },
    { to: "/quotes", label: "Quotes (outside work)", perms: ["quote.request", "jobcard.approve", "float.approve"] },
  ] },
  { group: "Registers", items: [
    { to: "/registers", label: "Trucks, drivers, routes…", perms: ["registers.view"] },
  ] },
  { group: "Books", items: [
    { to: "/books", label: "Accounts dashboard", perms: ["books.view"] },
    { to: "/journals", label: "Journals", perms: ["books.view"] },
    { to: "/reports", label: "Reports", perms: ["books.view"], match: ["/reports", "/ledger"] },
    { to: "/statements", label: "Statements", perms: ["books.view"] },
    { to: "/accounts", label: "Chart of accounts", perms: ["books.view"] },
  ] },
  { group: "System", items: [
    { to: "/users", label: "Users", perms: ["users.manage"] },
    { to: "/roles", label: "Roles & approvals", perms: ["roles.manage"] },
    { to: "/audit", label: "Audit trail", perms: ["audit.view"] },
    { to: "/setup", label: "Company setup", perms: ["books.view"] },
  ] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(0);
  const perms = user?.perms || [];
  const can = (p) => !p || p.some((x) => perms.includes(x));
  useEffect(() => {
    api.get("/approvals/inbox").then((r) => setWaiting(r.data.length)).catch(() => {});
  }, [loc.pathname]);
  const active = (n) => (n.exact ? loc.pathname === n.to : (n.match || [n.to]).some((m) => loc.pathname.startsWith(m)));
  return (
    <div className="min-h-screen bg-[#F6F5F3] text-[#1C1B1A]">
      <header className="flex flex-wrap items-center gap-3 bg-[#14231A] px-4 py-3 text-white md:px-6">
        <Link to="/" className="flex min-w-[200px] flex-1 items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand font-bold" aria-hidden="true">M</div>
          <div>
            <div className="text-[15px] font-semibold">Montrose</div>
            <div className="text-xs text-[#B9C7BE]">{user?.name || user?.email} · {user?.role_label}</div>
          </div>
        </Link>
        {can(["trips.manage"]) && <Link to="/trips?new=1" className="flex h-10 items-center rounded-full bg-brand px-4 text-sm font-semibold text-white hover:bg-brand/90">+ Dispatch truck</Link>}
        {can(["expense.request", "float.request"]) && <Link to="/spend?new=1" className="flex h-10 items-center rounded-full border border-white/30 px-4 text-sm font-semibold text-white hover:bg-white/10">+ Request money</Link>}
      </header>
      <div className="flex flex-wrap">
        <nav aria-label="Main" className="w-full border-b border-[#E4E2DE] bg-white px-3 py-3 md:min-h-[calc(100vh-64px)] md:w-[230px] md:border-b-0 md:border-r md:py-4">
          <div className="flex flex-wrap gap-1 md:flex-col md:gap-0.5">
            {NAV.map((g) => {
              const items = g.items.filter((n) => can(n.perms));
              if (!items.length) return null;
              return (
                <div key={g.group || "top"} className="contents md:block">
                  {g.group && <div className="hidden px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400 md:block">{g.group}</div>}
                  {items.map((n) => (
                    <Link key={n.to} to={n.to} aria-current={active(n) ? "page" : undefined}
                      className={`flex items-center justify-between rounded-md px-3 py-2.5 text-sm md:py-2 ${active(n) ? "bg-[#E7F0EA] font-semibold text-brand" : "text-[#1C1B1A] hover:bg-[#F6F5F3]"}`}>
                      <span>{n.label}</span>
                      {n.badge && waiting > 0 && <span className="ml-2 rounded-full bg-rose-600 px-2 text-xs font-semibold text-white">{waiting}</span>}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
          <div className="mt-6 hidden px-3 text-xs text-[#5F5C58] md:block">
            <button type="button" className="text-brand hover:underline" onClick={async () => { await logout(); navigate("/login"); }}>Sign out</button>
          </div>
        </nav>
        <main className="min-w-0 flex-[999_1_560px]">{children}</main>
      </div>
    </div>
  );
}
